import { useEffect, useState } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'

import { useFetchListCategoriesQuery } from '../../../redux-toolkits/lists/list.slice'
import { ListCategoryDoc } from '../../../redux-toolkits/categories/categories.type'
import {
  QueryProps,
  StorefrontSection,
} from '../../../redux-toolkits/shared/shared.type'
import { Status } from '../../cards/statusTag'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import { TableComponent } from '../../table'

type V = ListCategoryDoc

const defaultquery = {
  search: undefined,
  skip: 0,
  limit: '100',
}

export const SelectCategories = ({
  maxSelection,
  open,
  categories,
  setCategories,
  activeOnly = false,
  section,
}: {
  maxSelection?: number
  open?: boolean
  categories: Partial<V>[]
  setCategories: (creators: V[]) => void
  activeOnly?: boolean
  section?: StorefrontSection
}) => {
  const [queryParam, setQueryParam] = useState<QueryProps>(defaultquery)

  const { data, isFetching } = useFetchListCategoriesQuery(
    { ...queryParam, isAdmin: !activeOnly, section },

    {
      refetchOnMountOrArgChange: true,
      skip: !open,
    },
  )

  return (
    <div
      className="
        flex flex-col gap-2
        cursor-pointer
        "
    >
      <div className="bg-white pb-2 !max-w-full sticky top-0 z-10">
        <SearchInput
          searchFunction={(search) => {
            setQueryParam({
              ...queryParam,
              search: search,
              skip: 0,
            })
          }}
          placeholder="Search Categories"
          ariaLabel="Search product categories"
          className="!max-w-full"
        />
      </div>

      <div className="w-full flex-1 overflow-y-scroll">
        <TableComponent
          rows={(
            data?.data?.data?.filter(
              (i) =>
                !activeOnly ||
                i.isActive ||
                categories.map((category) => category._id).includes(i._id),
            ) ?? []
          )?.map((category) => {
            return {
              id: category._id,
              content: [
                <Checkbox
                  disabled={
                    maxSelection !== undefined &&
                    categories.length >= maxSelection &&
                    categories.length != 1 &&
                    !categories.map((c) => c._id).includes(category._id)
                  }
                  checkStatus={categories
                    .map((category) => category._id)
                    .includes(category._id)}
                  handleChange={(checked) => {
                    if (checked) {
                      const categoriesCopy: ListCategoryDoc[] = JSON.parse(
                        JSON.stringify(categories),
                      )
                      if (
                        maxSelection !== undefined &&
                        categoriesCopy.length >= maxSelection
                      ) {
                        categoriesCopy.shift()
                      }
                      categoriesCopy.push(category)

                      setCategories(categoriesCopy)
                    } else {
                      const categoriesCopy: ListCategoryDoc[] = JSON.parse(
                        JSON.stringify(categories),
                      )
                      setCategories(
                        categoriesCopy.filter(
                          (singleCategory) =>
                            category._id !== singleCategory._id,
                        ),
                      )
                    }
                  }}
                  text={
                    <div className="flex items-center gap-3">
                      {category.icon && (
                        <img
                          src={category.icon}
                          className="block w-[48px] h-[48px] object-cover rounded-md"
                          alt={`icon`}
                        />
                      )}

                      <span>{category.name}</span>

                      <Status
                        type={category.isActive ? 'success' : 'fail'}
                        text={category.isActive ? 'Active' : 'Inactive'}
                        className="ml-auto"
                      />
                    </div>
                  }
                  textPosition="right"
                  className="py-1 border-b last:border-b-0 w-full"
                  key={category._id}
                />,
              ],
            }
          })}
          loading={isFetching}
          isEmpty={[0, undefined].includes(data?.data?.data?.length)}
        />
      </div>
    </div>
  )
}

export const SelectCategoriesModal = ({
  open,
  setOpen,
  onCloseCallback,
  onSuccessCallback,
  selectedCategories = [],
  maxSelection,
  loading,
  buttonText = 'Continue',
  activeOnly = false,
  section,
}: {
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: ListCategoryDoc[]) => void
  selectedCategories?: ListCategoryDoc[]
  maxSelection?: number
  loading?: boolean
  buttonText?: string
  activeOnly?: boolean
  section?: StorefrontSection
}) => {
  const [categories, setCategories] = useState<ListCategoryDoc[]>([])

  useEffect(() => {
    if (open) {
      setCategories(selectedCategories)
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback && onCloseCallback()
        setCategories(selectedCategories)
      }}
      title="Select Categories"
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        <SelectCategories
          maxSelection={maxSelection}
          open={open}
          categories={categories}
          setCategories={setCategories}
          activeOnly={activeOnly}
          section={section}
        />

        <Button
          type="button"
          primary
          label={buttonText}
          onClick={() => {
            onSuccessCallback && onSuccessCallback(categories)
          }}
          className="sticky bottom-0"
          loading={loading}
          disabled={loading}
        />
      </div>
    </ModalComponent>
  )
}
